import {
  ColumnFactory,
  createDataQualityCellFormatter,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  DataQualityAssetType,
  DataQualityCellFormatterRowDataFunctions,
  DataQualityIconHeaderCertificationFormatterFactory,
  DataQualityIconHeaderFormatterFactory,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Formatters, Messages } from '../../../gen-src/Localize';
import { VirtualConnectionOrderField } from '../../../gen-src/queries/globalTypes';
import { VirtualConnectionsDisplayGridRowData } from '../database/types/VirtualConnectionDisplayGridRowData';
import { createLinkOrTextCellFormatter } from '../datagrid/cellFormatters/LinkCellFormatter';
import { createObfuscatedLinkCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import { GlobalAssetGridDC3ColumnFactoryParams } from '../sorting/makeColumnFactoryParams';
import { VirtualConnectionsValidSortFields } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';

const shouldBeObfuscated: (rowData: VirtualConnectionsDisplayGridRowData) => boolean = ({ name }) => name === null;
const projectContainerType = 'Project';

type VirtualConnectionsColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<VirtualConnectionsValidSortFields>,
  VirtualConnectionsDisplayGridRowData,
  VirtualConnectionsValidSortFields
>;

const rowDataFunctions: DataQualityCellFormatterRowDataFunctions<VirtualConnectionsDisplayGridRowData> = {
  getAssetUrlFromRowData: (urls: SiteUrls, { id }) => urls.virtualConnectionUrl(id),
  getNameFromRowData: ({ name }) => name || '',
  getTypeNameFromRowData: (): DataQualityAssetType => 'VirtualConnection',
  getDataQualityCertificationFromRowData: ({ dataQualityCertifications }) => dataQualityCertifications
}


function columns(): ReadonlyArray<VirtualConnectionsColumnFactory> {
  const cols: ReadonlyArray<VirtualConnectionsColumnFactory> = [
    () => ({
      name: 'certification',
      dataKey: '',
      headerText: '',
      headerTooltip: Messages.Certification(),
      headerFormatter: DataQualityIconHeaderCertificationFormatterFactory,
      cellFormatter: createDataQualityCellFormatter({
        getWarningsFromRowData: ({ dataQualityWarnings }) => dataQualityWarnings,
        disableIconOnlyTabIndex: true,
        isCertification: true,
        rowDataFunctions: rowDataFunctions,
      }),
      sortable: false,
      resizable: false,
    }),
    () => ({
      name: 'warning',
      dataKey: '',
      headerText: '',
      headerTooltip: Messages.DataQualityWarningTitle(),
      headerFormatter: DataQualityIconHeaderFormatterFactory,
      cellFormatter: createDataQualityCellFormatter({
        getWarningsFromRowData: ({ dataQualityWarnings }) => dataQualityWarnings,
        disableIconOnlyTabIndex: true,
        isCertification: false,
        rowDataFunctions: rowDataFunctions,
      }),
      sortable: false,
      resizable: false,
    }),
    (sortParams) => ({
      name: 'name',
      dataKey: VirtualConnectionOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { vizportalUrlId }) => urls.virtualConnectionLineagePath(vizportalUrlId)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'workbooks',
      dataKey: VirtualConnectionOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
      headerText: Messages.GridHeaderWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamWorkbooksConnection }) =>
          downstreamWorkbooksConnection
            ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
            : null
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'datasources',
      dataKey: VirtualConnectionOrderField.DOWNSTREAM_DATASOURCES_COUNT,
      headerText: Messages.GridHeaderDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamDatasourcesConnection }) =>
          downstreamDatasourcesConnection
            ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
            : null
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'location',
      dataKey: VirtualConnectionOrderField.CONTAINER_NAME,
      headerText: Messages.GridHeaderLocation(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkOrTextCellFormatter(
        ({ containerType, containerName }) => {
          return containerType === projectContainerType ? containerName : Messages.PersonalSpaceName();
        },
        (urls, { projectVizportalUrlId, containerType }) => (projectVizportalUrlId && containerType === projectContainerType ? urls.projectPath(projectVizportalUrlId) : null)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderOwner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(({ owner }) => owner),
      resizable: true,
      sortable: false,
    }),
  ];

  return cols.filter(Boolean) as ReadonlyArray<VirtualConnectionsColumnFactory>;
}

export type VirtualConnectionsDisplayGridProps = UsualDisplayGridProps<
  VirtualConnectionsDisplayGridRowData,
  GraphQLError
> &
  GridSortProps<VirtualConnectionsValidSortFields>;

export function VirtualConnectionsDisplayGrid(props: VirtualConnectionsDisplayGridProps): JSX.Element {
  const cols = columns().map((columnFactory) => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
